import { CheckVerifiedIcon as CheckVerifiedDuocolorIcon } from '@bettermode/icons/duocolor/CheckVerified'
import { CheckVerifiedIcon as CheckVerifiedLineIcon } from '@bettermode/icons/line/CheckVerified'
import { CheckVerifiedIcon as CheckVerifiedSolidIcon } from '@bettermode/icons/solid/CheckVerified'
import { AppearanceStyle } from '@tribeplatform/design-system/types'
import { MemberAvatarPlaceholder } from '@tribeplatform/react-components/common/components/member'
import { T, useI18n } from '@tribeplatform/react-components/i18n'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { RadioGroup } from '@tribeplatform/react-ui-kit/RadioGroup'
import { SectionHeader } from '@tribeplatform/react-ui-kit/SectionHeader'

import { useCustomizer, useCustomizerTheme } from '../hooks/index.js'
import { CustomizerActionType } from '../types.js'
import { StyleSelector } from './components/StyleSelector.js'
import {
  CardBorderedIcon,
  CardElevatedIcon,
  CardPlainIcon,
  CornerRoundIcon,
  CornerSharpIcon,
} from './icons.js'
import {
  getTranslatedAvatarShapeStyles,
  getTranslatedAvatarStyles,
  getTranslatedButtonStyles,
  getTranslatedCardStyles,
  getTranslatedCornerStyles,
  getTranslatedIconStyles,
} from './utils.js'

const AVATAR_PLACEHOLDER_NAME = 'A B'

export const AppearanceStyles = () => {
  const { $t } = useI18n()
  const { dispatch } = useCustomizer()
  const {
    appearance: { style },
  } = useCustomizerTheme()

  const updateStyle = (newStyle: Partial<AppearanceStyle>) => {
    dispatch({
      type: CustomizerActionType.NEW_UPDATE,
      payload: {
        theme: {
          typography: {
            style: {
              ...style,
              ...newStyle,
            },
          },
        },
      },
    })
  }

  const setCorners = (value: AppearanceStyle['corners']) => {
    updateStyle({
      corners: value,
    })
  }

  const setCards = (value: AppearanceStyle['cards']) => {
    updateStyle({
      cards: value,
    })
  }

  const setButtons = (value: AppearanceStyle['buttons']) => {
    const avatarShape: AppearanceStyle['avatarShape'] =
      value === 'pill' ? 'full-rounded' : value

    updateStyle({
      buttons: value,
      avatarShape,
    })
  }

  const setAvatarShape = (value: AppearanceStyle['avatarShape']) => {
    updateStyle({
      avatarShape: value,
    })
  }

  const setAvatars = (value: AppearanceStyle['avatars']) => {
    updateStyle({
      avatars: value,
    })
  }

  const setIcons = (value: AppearanceStyle['icons']) => {
    updateStyle({
      icons: value,
    })
  }

  return (
    <div className="space-y-10">
      <div className="space-y-4">
        <SectionHeader
          title={$t({
            defaultMessage: 'Corners',
            id: 'Admin.Appearance.Corners',
          })}
          description={$t({
            defaultMessage:
              'Choose the shape of the cards, inputs, alerts, modal and other similar components.',
            id: 'Admin.Appearance.Corners.Description',
          })}
          size="sm"
        />

        <RadioGroup value={style.corners} onChange={setCorners} variant="cards">
          <RadioGroup.Items>
            <RadioGroup.Item value="round">
              <StyleSelector
                title={getTranslatedCornerStyles($t, 'round')}
                icon={<CornerRoundIcon />}
              />
            </RadioGroup.Item>
            <RadioGroup.Item value="sharp">
              <StyleSelector
                title={getTranslatedCornerStyles($t, 'sharp')}
                icon={<CornerSharpIcon />}
              />
            </RadioGroup.Item>
          </RadioGroup.Items>
        </RadioGroup>
      </div>
      <div className="space-y-4">
        <SectionHeader
          title={$t({
            defaultMessage: 'Cards',
            id: 'Admin.Appearance.Cards',
          })}
          description={$t({
            defaultMessage:
              'Choose your preferred card styles that best fits your brand',
            id: 'Admin.Appearance.Cards.Description',
          })}
          size="sm"
        />

        <RadioGroup value={style.cards} onChange={setCards} variant="cards">
          <RadioGroup.Items>
            <RadioGroup.Item value="plain">
              <StyleSelector
                title={getTranslatedCardStyles($t, 'plain')}
                icon={<CardPlainIcon />}
              />
            </RadioGroup.Item>
            <RadioGroup.Item value="bordered">
              <StyleSelector
                title={getTranslatedCardStyles($t, 'bordered')}
                icon={<CardBorderedIcon />}
              />
            </RadioGroup.Item>
            <RadioGroup.Item value="elevated">
              <StyleSelector
                title={getTranslatedCardStyles($t, 'elevated')}
                icon={<CardElevatedIcon />}
              />
            </RadioGroup.Item>
          </RadioGroup.Items>
        </RadioGroup>
      </div>
      <div className="space-y-4">
        <SectionHeader
          title={$t({
            defaultMessage: 'Button',
            id: 'Admin.Appearance.Button',
          })}
          size="sm"
        />

        <RadioGroup value={style.buttons} onChange={setButtons} variant="cards">
          <RadioGroup.Items>
            <RadioGroup.Item value="pill">
              <StyleSelector
                title={getTranslatedButtonStyles($t, 'pill')}
                icon={
                  <Button
                    size="sm"
                    variant="primary"
                    className="rounded-full pointer-events-none"
                  >
                    <T defaultMessage="Action" id="Generics.Action" />
                  </Button>
                }
              />
            </RadioGroup.Item>
            <RadioGroup.Item value="rounded">
              <StyleSelector
                title={getTranslatedButtonStyles($t, 'rounded')}
                icon={
                  <Button
                    size="sm"
                    variant="primary"
                    className="rounded-md pointer-events-none"
                  >
                    <T defaultMessage="Action" id="Generics.Action" />
                  </Button>
                }
              />
            </RadioGroup.Item>
            <RadioGroup.Item value="sharp">
              <StyleSelector
                title={getTranslatedButtonStyles($t, 'sharp')}
                icon={
                  <Button
                    size="sm"
                    variant="primary"
                    className="rounded-none pointer-events-none"
                  >
                    <T defaultMessage="Action" id="Generics.Action" />
                  </Button>
                }
              />
            </RadioGroup.Item>
          </RadioGroup.Items>
        </RadioGroup>
      </div>

      <div className="space-y-4">
        <SectionHeader
          title={$t({
            defaultMessage: 'Avatar placeholder',
            id: 'Admin.Appearance.AvatarPlaceholder',
          })}
          size="sm"
        />

        <RadioGroup value={style.avatars} onChange={setAvatars} variant="cards">
          <RadioGroup.Items>
            <RadioGroup.Item value="initials">
              <StyleSelector
                title={getTranslatedAvatarStyles($t, 'initials')}
                icon={
                  <MemberAvatarPlaceholder
                    size="2x"
                    avatarStyle="initials"
                    name={AVATAR_PLACEHOLDER_NAME}
                  />
                }
              />
            </RadioGroup.Item>
            <RadioGroup.Item value="cute">
              <StyleSelector
                title={getTranslatedAvatarStyles($t, 'cute')}
                icon={
                  <MemberAvatarPlaceholder
                    size="2x"
                    avatarStyle="cute"
                    name={AVATAR_PLACEHOLDER_NAME}
                  />
                }
              />
            </RadioGroup.Item>
            <RadioGroup.Item value="neutral">
              <StyleSelector
                title={getTranslatedAvatarStyles($t, 'neutral')}
                icon={
                  <MemberAvatarPlaceholder
                    size="2x"
                    avatarStyle="neutral"
                    name={AVATAR_PLACEHOLDER_NAME}
                  />
                }
              />
            </RadioGroup.Item>
          </RadioGroup.Items>
        </RadioGroup>
      </div>

      <div className="space-y-4">
        <SectionHeader
          title={$t({
            defaultMessage: 'Avatar corner',
            id: 'Admin.Appearance.AvatarCorner',
          })}
          size="sm"
        />

        <RadioGroup
          value={style.avatarShape}
          onChange={setAvatarShape}
          variant="cards"
        >
          <RadioGroup.Items>
            <RadioGroup.Item value="full-rounded">
              <StyleSelector
                title={getTranslatedAvatarShapeStyles($t, 'full-rounded')}
                icon={
                  <MemberAvatarPlaceholder
                    size="2x"
                    avatarStyle={style.avatars}
                    name={AVATAR_PLACEHOLDER_NAME}
                    avatarShapeStyle="full-rounded"
                  />
                }
              />
            </RadioGroup.Item>
            <RadioGroup.Item value="rounded">
              <StyleSelector
                title={getTranslatedAvatarShapeStyles($t, 'rounded')}
                icon={
                  <MemberAvatarPlaceholder
                    size="2x"
                    avatarStyle={style.avatars}
                    name={AVATAR_PLACEHOLDER_NAME}
                    avatarShapeStyle="rounded"
                  />
                }
              />
            </RadioGroup.Item>
            <RadioGroup.Item value="sharp">
              <StyleSelector
                title={getTranslatedAvatarShapeStyles($t, 'sharp')}
                icon={
                  <MemberAvatarPlaceholder
                    size="2x"
                    avatarStyle={style.avatars}
                    name={AVATAR_PLACEHOLDER_NAME}
                    avatarShapeStyle="sharp"
                  />
                }
              />
            </RadioGroup.Item>
          </RadioGroup.Items>
        </RadioGroup>
      </div>

      <div className="space-y-4">
        <SectionHeader
          title={$t({
            defaultMessage: 'Icon style',
            id: 'Admin.Appearance.IconStyle',
          })}
          size="sm"
        />

        <RadioGroup value={style.icons} onChange={setIcons} variant="cards">
          <RadioGroup.Items>
            <RadioGroup.Item value="line">
              <StyleSelector
                title={getTranslatedIconStyles($t, 'line')}
                icon={<CheckVerifiedLineIcon className="h-[1.5em] w-[1.5em]" />}
              />
            </RadioGroup.Item>
            <RadioGroup.Item value="solid">
              <StyleSelector
                title={getTranslatedIconStyles($t, 'solid')}
                icon={
                  <CheckVerifiedSolidIcon className="h-[1.5em] w-[1.5em]" />
                }
              />
            </RadioGroup.Item>
            <RadioGroup.Item value="duocolor">
              <StyleSelector
                title={getTranslatedIconStyles($t, 'duocolor')}
                icon={
                  <CheckVerifiedDuocolorIcon className="h-[1.5em] w-[1.5em]" />
                }
              />
            </RadioGroup.Item>
          </RadioGroup.Items>
        </RadioGroup>
      </div>
    </div>
  )
}
